import React, { createContext, useContext, useState, ReactNode } from 'react';
import SaveErrorModal from '../components/SaveErrorModal';

interface SaveErrorContextType {
  showSaveError: (message?: string, onRetry?: () => void) => void;
}

const SaveErrorContext = createContext<SaveErrorContextType | undefined>(undefined);

export const useSaveError = () => {
  const context = useContext(SaveErrorContext);
  if (!context) {
    throw new Error('useSaveError must be used within SaveErrorProvider');
  }
  return context;
};

interface SaveErrorProviderProps {
  children: ReactNode;
}

export const SaveErrorProvider: React.FC<SaveErrorProviderProps> = ({ children }) => {
  const [showModal, setShowModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [retryHandler, setRetryHandler] = useState<(() => void) | undefined>(undefined);

  const showSaveError = (message?: string, onRetry?: () => void) => {
    setErrorMessage(message || '');
    setRetryHandler(() => onRetry);
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setErrorMessage('');
    setRetryHandler(undefined);
  };

  const handleRetry = () => {
    if (retryHandler) {
      retryHandler();
    }
    handleClose();
  };

  return (
    <SaveErrorContext.Provider value={{ showSaveError }}>
      {children}
      <SaveErrorModal
        isOpen={showModal}
        onClose={handleClose}
        onRetry={retryHandler ? handleRetry : undefined}
        errorMessage={errorMessage}
      />
    </SaveErrorContext.Provider>
  );
};
